import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, DatePicker, Select, Textarea } from '~/components/primitives';
import { apiRequest } from '~/lib/api/client';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import { fetchLandedCostById } from '~/lib/api/landed-cost';
import shared from './_auth._shared.module.css';

/**
 * EFE-2 — Landed Cost edit (header-only: posting_datetime + allocation_method + remarks).
 * Charges + targets — read-only display, sent back unchanged via PUT to preserve
 * BE invariant (LandedCostCreateInput requires both arrays).
 */
export const Route = createFileRoute('/_auth/landed-cost/$id/edit')({
  component: LcEdit,
});

type AllocationMethod = 'by_amount' | 'by_qty' | 'by_weight';

function LcEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['landed-cost', id],
    queryFn: () => fetchLandedCostById(id),
  });

  const [form, setForm] = useState({
    posting_at: '',
    allocation_method: 'by_amount' as AllocationMethod,
    voucher_no: '',
    notes: '',
  });
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    if (query.data && !primed) {
      const lc = query.data;
      setForm({
        posting_at: lc.posting_at.slice(0, 10),
        // GET response doesn't include allocation_method; default per-charge basis.
        allocation_method: (lc.charges[0]?.basis ?? 'by_amount') as AllocationMethod,
        voucher_no: lc.code,
        notes: lc.notes ?? '',
      });
      setPrimed(true);
    }
  }, [query.data, primed]);

  const updateMut = useMutation({
    mutationFn: () => {
      const lc = query.data;
      if (!lc) throw new Error('LC not loaded');
      // EFE-2: reconstruct LandedCostCreateInput payload — pass charges + targets
      // unchanged (header-only edit). source_items now includes stock_entry_item_id
      // + warehouse_id + batch_id from BE extension.
      const charges = lc.charges.map((ch) => ({
        charge_type: 'other' as const,
        description: ch.title,
        amount: { amount: ch.amount, currency: ch.currency },
      }));
      const targets = lc.source_items.map((src) => {
        const s = src as unknown as {
          stock_entry_id: string;
          stock_entry_item_id?: string;
          item_id: string;
          warehouse_id?: string;
          batch_id?: string | null;
          qty: string;
          amount: string;
        };
        return {
          source_stock_entry_id: s.stock_entry_id,
          source_stock_entry_item_id: s.stock_entry_item_id ?? s.stock_entry_id,
          item_id: s.item_id,
          warehouse_id: s.warehouse_id ?? s.stock_entry_id,
          batch_id: s.batch_id ?? null,
          qty: s.qty,
          base_amount: { amount: s.amount, currency: lc.currency },
        };
      });
      const body = {
        voucher_no: form.voucher_no,
        posting_datetime: new Date(`${form.posting_at}T00:00:00Z`).toISOString(),
        allocation_method: form.allocation_method,
        charges,
        targets,
        remarks: form.notes || null,
      };
      return apiRequest(`/landed-cost/${encodeURIComponent(id)}`, z.unknown(), {
        method: 'PUT',
        body,
      });
    },
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.lc_toast_updated() });
      void qc.invalidateQueries({ queryKey: ['landed-cost'] });
      void navigate({ to: '/landed-cost/$id', params: { id } });
    },
    onError: (err: unknown) =>
      toast.push({
        variant: 'error',
        title: m.lc_toast_update_failed(),
        description: renderApiErrorMessage(err),
      }),
  });

  if (query.isLoading) return <div>…</div>;
  if (!query.data) return <div>Not found</div>;

  const lc = query.data;
  const readOnly = lc.status !== 'draft';

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.nav_landed_cost(), to: '/landed-cost' },
              { label: lc.code, to: `/landed-cost/${id}` },
              { label: m.common_button_edit() },
            ]}
          />
        }
        // FE-74: edit page H1 includes voucher_no для identity.
        title={`${m.lc_edit_title()} · ${lc.code}`}
      />
      {readOnly && (
        <div className={[shared.inlineAlert, shared.inlineAlertWarn].join(' ')} role="status">
          {m.error_entity_not_editable({
            current_state: lc.status,
            editable_states: 'draft',
          })}
        </div>
      )}
      <div className={shared.formColumn480}>
        <DatePicker
          label={m.invoice_field_issue_date()}
          value={form.posting_at || null}
          onChange={(next) => setForm({ ...form, posting_at: next ?? '' })}
          disabled={readOnly}
        />
        <Select
          label="Метод распределения"
          value={form.allocation_method}
          onChange={(e) =>
            setForm({ ...form, allocation_method: e.target.value as AllocationMethod })
          }
          disabled={readOnly}
          hint="По сумме = пропорционально стоимости поступления. По количеству = пропорционально qty. По весу = по физической массе позиций."
          options={[
            { value: 'by_amount', label: 'По сумме' },
            { value: 'by_qty', label: 'По количеству' },
            { value: 'by_weight', label: 'По весу' },
          ]}
        />
        <Textarea
          label={m.invoice_field_notes()}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          disabled={readOnly}
          rows={3}
        />
      </div>
      <div className={shared.stickyFooter}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate({ to: '/landed-cost/$id', params: { id } })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => updateMut.mutate()}
          loading={updateMut.isPending}
          disabled={readOnly}
        >
          {m.common_button_save()}
        </Button>
      </div>
    </div>
  );
}
